import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, PaymentBadge } from "@/components/StatusBadge";
import { api, errMsg, inr, fmtDay, fmtDateTime } from "@/lib/api";
import { toDDMMYYYY } from "@/components/DatePicker";

export const useAdminList = (url, params) => {
  const [items, setItems] = useState(null);
  const key = JSON.stringify(params || {});
  const load = () => api.get(url, { params }).then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  useEffect(() => {
    load();
  }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return [items, load];
};

export const Spin = () => <Loader2 className="animate-spin text-[#0D5C46]" />;

export const StatusFilter = ({ value, onChange, options, testId }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className="w-44" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="all">All</SelectItem>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

export const Applications = () => {
  const [status, setStatus] = useState("pending");
  const [items, load] = useAdminList("/admin/applications", status ? { status } : {});
  const act = async (c, action) => {
    try {
      await api.post(`/admin/companions/${c.id}/action`, { action, note: action === "reject" ? "Verification could not be completed" : null });
      toast.success(`Profile ${action}d`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4">
      <StatusFilter value={status} onChange={setStatus} options={["pending", "approved", "rejected", "suspended"]} testId="applications-status-filter" />
      {items === null ? <Spin /> : items.length === 0 ? <p className="text-slate-500 py-6">No applications.</p> : items.map((c) => (
        <div key={c.id} className="card-surface p-5 grid md:grid-cols-4 gap-4" data-testid={`application-row-${c.display_name}`}>
          <div className="md:col-span-3 space-y-1 text-sm">
            <div className="flex flex-wrap gap-2 items-center"><b className="text-base">{c.display_name}</b><span className="chip">{c.status}</span>{c.is_demo && <span className="demo-badge">DEMO</span>}</div>
            <p className="text-slate-600">{c.legal_name} · {c.email} · {c.phone ? <a href={`tel:+91${c.phone}`} className="underline" data-testid={`application-phone-${c.id}`}>+91 {c.phone}</a> : "—"} · {c.gender} · age {c.age} (DOB {toDDMMYYYY(c.dob)}) · {c.city}</p>
            <p className="text-slate-600">ID: {c.id_verification.doc_type} {c.id_verification.masked} · <b>{c.id_verification.status}</b> · {inr(c.hourly_rate)}/hr · {c.activities.join(", ")}</p>
            <p className="text-slate-500 text-xs">{c.bio}</p>
          </div>
          <div className="flex md:flex-col gap-2 md:items-end">
            {c.status === "pending" && <><Button size="sm" data-testid="admin-approve-application-btn" onClick={() => act(c, "approve")}>Approve</Button><Button size="sm" variant="outline" data-testid="admin-reject-application-btn" onClick={() => act(c, "reject")}>Reject</Button></>}
            {c.status === "approved" && <Button size="sm" variant="outline" className="text-rose-600" data-testid="admin-suspend-btn" onClick={() => act(c, "suspend")}>Suspend</Button>}
            {c.status === "suspended" && <Button size="sm" variant="outline" data-testid="admin-reinstate-btn" onClick={() => act(c, "reinstate")}>Reinstate</Button>}
          </div>
        </div>
      ))}
    </div>
  );
};

const PAY_NEXT = { collected: ["confirm", "dispute", "refund_pending"], confirmed: ["dispute", "refund_pending"], disputed: ["resolve", "refund_pending"], refund_pending: ["refunded"] };
const PAY_LABEL = { collected: "Cash collected", confirm: "Confirm payment", dispute: "Dispute payment", resolve: "Resolve", refund_pending: "Refund pending", refunded: "Mark refunded" };

const PaymentActions = ({ b, onDone, canPayout }) => {
  const run = async (url, body, msg) => {
    try {
      await api.post(url, body);
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const payout = () => {
    const reference = window.prompt("Payout reference (UPI/bank/cash receipt no.)");
    if (reference) run(`/admin/bookings/${b.id}/payout`, { reference }, "Payout recorded");
  };
  const next = b.payment_status === "pending" ? (["confirmed", "completed", "disputed"].includes(b.status) ? ["collected"] : []) : PAY_NEXT[b.payment_status] || [];
  return (
    <>
      {next.map((a) => (
        <Button key={a} size="sm" variant="outline" data-testid={`admin-pay-${a}-${b.ref}`} onClick={() => run(`/admin/bookings/${b.id}/payment`, { action: a }, PAY_LABEL[a])}>{PAY_LABEL[a]}</Button>
      ))}
      {canPayout && b.status === "completed" && b.payment_status === "confirmed" && b.payout_status !== "paid" && <Button size="sm" data-testid={`admin-payout-btn-${b.ref}`} onClick={payout}>Record payout ₹{b.pricing.companion_share}</Button>}
      {b.payout_status === "paid" && <span className="text-xs text-emerald-700" data-testid={`admin-payout-done-${b.ref}`}>Payout paid</span>}
    </>
  );
};

const RunReminders = ({ onDone }) => {
  const run = async () => {
    try {
      const { data } = await api.post("/admin/reminders/run");
      toast.success(`Reminders sent for ${data.sent} booking(s)`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return <Button size="sm" variant="outline" onClick={run} data-testid="admin-run-reminders-btn">Send due reminders now</Button>;
};

export const Bookings = ({ canRefund }) => {
  const [status, setStatus] = useState("");
  const [items, load] = useAdminList("/admin/bookings", status ? { status } : {});
  const act = async (b, action) => {
    try {
      await api.post(`/admin/bookings/${b.id}/status`, { action, reason: action === "cancel" ? "Cancelled by admin" : null });
      toast.success(`Booking ${action}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <StatusFilter value={status} onChange={setStatus} options={["pending", "accepted", "confirmed", "completed", "disputed", "rejected", "cancelled"]} testId="bookings-status-filter" />
        <RunReminders onDone={load} />
        <span className="text-xs text-slate-500">Auto reminders run every 15 min for confirmed meetups inside the reminder window (set in Pricing & Commission).</span>
      </div>
      <div className="overflow-x-auto card-surface">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-3">Ref</th><th className="p-3">Customer</th><th className="p-3">Companion</th><th className="p-3">When (IST)</th><th className="p-3">Total</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
          <tbody>
            {items === null ? <tr><td className="p-4"><Spin /></td></tr> : items.map((b) => (
              <tr key={b.id} className="border-t align-top" data-testid={`admin-booking-row-${b.ref}`}>
                <td className="p-3 font-mono text-xs">{b.ref}{b.is_demo && <span className="demo-badge ml-1">DEMO</span>}</td>
                <td className="p-3">{b.customer?.name}<p className="text-xs text-slate-500">{b.customer?.email}</p>{b.customer?.phone && <p className="text-xs font-mono" data-testid={`booking-customer-phone-${b.ref}`}>+91 {b.customer.phone}</p>}</td>
                <td className="p-3">{b.companion?.display_name}{b.companion?.phone && <p className="text-xs font-mono" data-testid={`booking-companion-phone-${b.ref}`}>+91 {b.companion.phone}</p>}</td>
                <td className="p-3 whitespace-nowrap">{fmtDay(b.date)}<p className="text-xs text-slate-500">{b.start_time} · {b.duration_hours}h</p></td>
                <td className="p-3 font-mono">{inr(b.pricing.total)}{b.refunded_amount > 0 && <p className="text-xs text-sky-700">−{inr(b.refunded_amount)}</p>}{b.cancellation?.refund_status === "pending_retry" && <p className="text-xs text-amber-700 font-semibold" data-testid={`admin-refund-pending-${b.ref}`}>Refund {inr(b.cancellation.refund_due)} pending — retry</p>}</td>
                <td className="p-3 space-y-1"><StatusBadge status={b.status} testId={`admin-status-${b.ref}`} /><br /><PaymentBadge status={b.payment_status} />{b.reminder_sent_at && <p className="text-[11px] text-emerald-700" data-testid={`admin-reminder-sent-${b.ref}`}>Reminder sent</p>}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {b.status === "accepted" && <Button size="sm" onClick={() => act(b, "confirm")} data-testid={`admin-confirm-btn-${b.ref}`}>Confirm</Button>}
                    {b.status === "confirmed" && <Button size="sm" variant="outline" onClick={() => act(b, "complete")} data-testid={`admin-complete-btn-${b.ref}`}>Complete</Button>}
                    {["confirmed", "completed"].includes(b.status) && <Button size="sm" variant="ghost" onClick={() => act(b, "dispute")} data-testid={`admin-dispute-btn-${b.ref}`}>Dispute</Button>}
                    {b.status === "disputed" && <Button size="sm" variant="outline" onClick={() => act(b, "resolve")} data-testid={`admin-resolve-btn-${b.ref}`}>Resolve</Button>}
                    {["pending", "accepted", "confirmed"].includes(b.status) && (canRefund || !["collected", "confirmed"].includes(b.payment_status)) && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => act(b, "cancel")} data-testid={`admin-cancel-btn-${b.ref}`}>Cancel</Button>}
                    {canRefund && <PaymentActions b={b} onDone={load} canPayout={canRefund} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const Tickets = () => {
  const [status, setStatus] = useState("");
  const [items, load] = useAdminList("/admin/tickets", status ? { status } : {});
  const update = async (t, s) => {
    try {
      await api.patch(`/admin/tickets/${t.id}`, { status: s, admin_note: `Marked ${s} by support` });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4">
      <StatusFilter value={status} onChange={setStatus} options={["open", "investigating", "resolved", "dismissed"]} testId="tickets-status-filter" />
      {items === null ? <Spin /> : items.length === 0 ? <p className="text-slate-500 py-6">No tickets.</p> : items.map((t) => (
        <div key={t.id} className={`card-surface p-5 text-sm space-y-2 ${t.priority === "urgent" ? "border-rose-300 bg-rose-50/40" : ""}`} data-testid="admin-ticket-row">
          <div className="flex flex-wrap gap-2 items-center"><span className="chip uppercase">{t.type}</span><b>{t.subject}</b>{t.priority === "urgent" && <span className="chip bg-rose-600 text-white border-rose-600">URGENT</span>}{t.is_demo && <span className="demo-badge">DEMO</span>}<span className="ml-auto chip">{t.status}</span></div>
          <p className="text-slate-600">{t.description}</p>
          <p className="text-xs text-slate-500">{t.contact_name} · {t.contact_email} {t.booking_ref && `· Booking ${t.booking_ref}`}{t.city && ` · City ${t.city}`}{t.preferred_date && ` · Preferred date ${toDDMMYYYY(t.preferred_date)}`} · {fmtDateTime(t.created_at)}</p>
          <div className="flex gap-2">{["investigating", "resolved", "dismissed"].map((s) => <Button key={s} size="sm" variant="outline" disabled={t.status === s} onClick={() => update(t, s)} data-testid={`ticket-${s}-btn`}>{s}</Button>)}</div>
        </div>
      ))}
    </div>
  );
};
